/**
 * Utility functions for performance optimization
 */

// Debounce function to limit how often a function can be called
export const debounce = <F extends (...args: any[]) => any>(
  func: F,
  waitFor: number
): ((...args: Parameters<F>) => void) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<F>): void => {
    if (timeout !== null) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => func(...args), waitFor);
  };
};

// Lazy load images when they enter the viewport
export const lazyLoadImage = (imgElement: HTMLImageElement, src: string): void => {
  const observer = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        imgElement.src = src;
        observer.disconnect();
      }
    });
  });
  
  observer.observe(imgElement);
};

// Image optimization helper
export const getOptimizedImageUrl = (url: string, width = 800): string => {
  if (!url) return url;
  
  // For Unsplash images, use their optimization API
  if (url.includes('images.unsplash.com')) {
    // Check if the URL already has parameters
    const hasParams = url.includes('?');
    const connector = hasParams ? '&' : '?';
    return `${url}${connector}w=${width}&q=80&auto=format`;
  }
  
  return url;
};

// Preload critical resources
export const preloadCriticalResources = (): void => {
  const resources = [
    { href: '/avn-logo.png', as: 'image' },
    { href: '/avn-logo-no-bg.png', as: 'image' },
    { href: 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&family=Open+Sans:wght@300;400;500;600;700&display=swap', as: 'style' }
  ];
  
  resources.forEach(resource => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = resource.href;
    link.as = resource.as;
    document.head.appendChild(link);
  });
};